import { DebugManager } from '@/managers/DebugManager'
import { SoundManager } from '@/managers/sound/SoundManager'
import GameSignals from '@/signals/GameSignals'
import { IntegrationAdapters } from '@modules/src/enums/IntegrationAdapters'
import { IModulesConfig } from '@modules/src/interfaces/IModulesConfig'
import { SPLASH_SCREEN_COMPONENT } from '@/gameConfigs/components/SplashScreenComponent'
import { BACKGROUND_COMPONENT } from '@/gameConfigs/components/BackgroundComponent'

import { LOGO_COMPONENT } from '@/gameConfigs/components/LogoComponent'
// import { INTRO_ANIMATION_COMPONENT } from '@/gameConfigs/components/IntroAnimationComponent'
import { FREE_SPINS_POPUPS_COMPONENT } from '@/gameConfigs/components/FreeSpinsPopupsComponent'
import { FREE_SPINS_SUMMARY_POPUPS_COMPONENT } from '@/gameConfigs/components/FreeSpinsSummaryPopupsComponent'
import { POPUPS_TRANSITION_COMPONENT } from '@/gameConfigs/components/PopupsTransitionComponent'
import { BUY_BONUS_COMPONENT } from '@/gameConfigs/components/BuyBonusComponent'
import { PANEL_COMPONENT } from '@/gameConfigs/components/PanelComponent'
import { TICKER_MESSAGE_COMPONENT } from '@/gameConfigs/components/TickerMessageComponent'
import { buildAssetUrl } from '@/utils/AssetsUrlUtil'

const ASSETS_BASED_PRELOADER_JSON = buildAssetUrl('/assets/json/PreloaderAssets.json')
const ASSETS_BASED_MAIN_JSON = buildAssetUrl('/assets/json/AssetsConfig.json')
const ASSETS_BASED_BG = buildAssetUrl('/assets/images/background.jpg')
const ASSETS_BASED_FREE_BG = buildAssetUrl('/assets/images/BackgroundFSLandscape.jpg')
const isLocalHost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
const TELEMETRY_TOGGLE_QUERY_PARAM = 'telemetry'
const TELEMETRY_TOGGLE_STORAGE_KEY = 'dg.telemetry.enabled'
const runtimeEnvRaw = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('env') || '' : ''
const runtimeHostRaw = typeof window !== 'undefined' ? window.location.hostname || '' : ''
const isStagingHost = runtimeHostRaw.toLowerCase().includes('staging')
const isLocalOrStagingRuntime = isLocalHost || isStagingHost
const telemetryToggleParam =
    typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get(TELEMETRY_TOGGLE_QUERY_PARAM)?.toLowerCase() || ''
        : ''
const telemetryEnabledFromStorage =
    typeof window !== 'undefined' ? window.localStorage.getItem(TELEMETRY_TOGGLE_STORAGE_KEY) : null
const telemetryEnabledFromQuery = ['1', 'true', 'on', 'yes'].includes(telemetryToggleParam)
const telemetryDisabledFromQuery = ['0', 'false', 'off', 'no'].includes(telemetryToggleParam)

if (typeof window !== 'undefined') {
    if (telemetryEnabledFromQuery) {
        window.localStorage.setItem(TELEMETRY_TOGGLE_STORAGE_KEY, 'true')
    } else if (telemetryDisabledFromQuery) {
        window.localStorage.setItem(TELEMETRY_TOGGLE_STORAGE_KEY, 'false')
    }
}

const isTelemetryEnabledOnLocalhost =
    telemetryEnabledFromQuery || (!telemetryDisabledFromQuery && telemetryEnabledFromStorage === 'true')
const isTelemetryEnabled = !isLocalHost || isTelemetryEnabledOnLocalhost
const SESSION_TELEMETRY_ENV =
    isLocalHost || ['stage', 'staging', 'lcl', 'local', 'dev', 'sit', 'uat'].includes(runtimeEnvRaw.toLowerCase()) || isStagingHost
        ? 'staging'
        : 'production'
const TELEMETRY_API_BASE_URL =
    SESSION_TELEMETRY_ENV === 'production'
        ? 'https://platform-api.dragongaming.com'
        : 'https://staging-platform-api.dragongaming.com'
const SESSION_TELEMETRY_ENDPOINT = `${TELEMETRY_API_BASE_URL}/api/v1/telemetry/events/session`
const ROUND_TELEMETRY_ENDPOINT = `${TELEMETRY_API_BASE_URL}/api/v1/telemetry/events/round`
const ECONOMY_TELEMETRY_ENDPOINT = `${TELEMETRY_API_BASE_URL}/api/v1/telemetry/events/economy`
const CANDYLAND_ML_TEMPLATE_URL =
    'https://staging-games.dragongaming.com/games/slots/candylandbonanza/v1.4.2/games/candylandbonanza/src/ml/{locale}.js?ver=20261212315569'

const ModulesConfig: IModulesConfig = {
    fastPlayPrompt: {
        threshold: 2,
    },
    sessionTelemetry: {
        enabled: isTelemetryEnabled,
        endpoint: SESSION_TELEMETRY_ENDPOINT,
        roundEndpoint: ROUND_TELEMETRY_ENDPOINT,
        economyEndpoint: ECONOMY_TELEMETRY_ENDPOINT,
        apiKey: 'dg_alert_staging_oQeaBBCHEQ8_rSHU2e8q1pPOqXWZY3GMCq6JQl7thg0',
        apiKeyHeader: 'X-API-Key',
        schemaVersion: 1,
        gameId: 'cash-cong-bonanza',
        build: '1.0.0',
        env: SESSION_TELEMETRY_ENV,
    },
    translations: {
        enabled: true,
        defaultLocale: 'en',
        translationLocations: [CANDYLAND_ML_TEMPLATE_URL],
    },
    assetLoader: {
        enabled: true,
        default: {
            preloadAssets: [ASSETS_BASED_PRELOADER_JSON],
            mainAssets: [ASSETS_BASED_MAIN_JSON],
        },
    },
    debugPanel: {
        urlParamsConfig: true,
        hiddenUrlParams: ['rtp', 'jurisdiction'],
        forceGsapTimelineTimeout: true,
        timelineConfig: true,
        forceDrawCallsPanel: false,
        countDrawCalls: true,
        folders: DebugManager.createFolders(),
        enabled: isLocalOrStagingRuntime || process.env.NODE_ENV !== 'production',
    },
    gameBackground: {
        enabled: true,
        backgrounds: [
            {
                url: ASSETS_BASED_BG,
                triggerSignals: [GameSignals.displaySplashScreenState, GameSignals.showBaseGameBackground],
            },
            {
                url: ASSETS_BASED_FREE_BG,
                triggerSignals: [GameSignals.showFreeSpinsBackground],
            },
        ],
    },
    components: {
        enabled: true,
        items: [
            BACKGROUND_COMPONENT,
            LOGO_COMPONENT,
            // INTRO_ANIMATION_COMPONENT,
            POPUPS_TRANSITION_COMPONENT,
            BUY_BONUS_COMPONENT,
            SPLASH_SCREEN_COMPONENT,
            PANEL_COMPONENT,
            TICKER_MESSAGE_COMPONENT,
            FREE_SPINS_POPUPS_COMPONENT,
            FREE_SPINS_SUMMARY_POPUPS_COMPONENT,
        ],
    },
    sounds: {
        enabled: true,
        soundsConfigPath: SoundManager.createConfigPath(),
        sounds: SoundManager.createSounds(),
        useAudioSprite: true,
    },
    bigWin: {
        enabled: true,
        customFont: {
            textFieldOffset: 0,
            fontName: 'freespinBitmap',
            fontSize: 16,
            letterSpacing: -18,
        },
    },
    integrations: {
        enabled: true,
        adapter: IntegrationAdapters.SLOTS_ADAPTER,
    },
}

export default ModulesConfig
